/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/notificationsPage.scss';

import React, {MouseEventHandler, useContext} from 'react';
import ReviewNotificationsController from "./reviewNotifications/reviewNotificationsController";
import {AppContext} from "../misc/appContext";
import {SubscriptionModuleName, SubscriptionModuleStatus} from "../../util/enums";
import {Loader} from '@dvrd/dvr-controls';
import InfoMessage from "../misc/messages/infoMessage";
import SubscriptionModule from "../../models/subscriptionModuleModel";
import IDate from '@dvrd/idate';
import Notification from "../../models/notificationModel";
import {BolmateButton} from "../controls";
import {renderPlural} from "../../util/utils";

interface Props {
    onActivateModule: MouseEventHandler;
    onSendTestNotification: (notification: Notification) => MouseEventHandler;
    onPulseModule: VoidFunction;
    onClickFeedback: MouseEventHandler;
    loading: boolean;
}

export default function NotificationsPage(props: Props) {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {onActivateModule, loading, onSendTestNotification, onPulseModule, onClickFeedback} = props;

    function renderModuleBanner() {
        const module = user?.subscription?.getModule(SubscriptionModuleName.NOTIFICATIONS);
        if (!module || !module.isActive()) return renderInactiveBanner();
        else if (module?.status === SubscriptionModuleStatus.TRIAL) return renderTrialBanner(module);
        return null;
    }

    function renderTrialBanner(module: SubscriptionModule) {
        const daysRemaining = Math.ceil(module.chargePerDate.diff(IDate.now(), 'days', true));
        return (
            <InfoMessage className='notifications-module-banner'>
                <div className='message-content'>
                    <p className='module-text'>
                        Met deze feature ontvang je meldingen van nieuwe reviews op jouw producten per e-mail.<br/>
                        Zo ben je altijd up-to-date en kun je indien nodig actie ondernemen en zo geen omzet
                        verliezen.<br/><br/>
                        Je hebt van ons 30 dagen proef gekregen, <b>je hebt nog {daysRemaining} {renderPlural(
                        daysRemaining, 'dag', 'dagen')} over.</b><br/>
                        Je kunt deze module nu al activeren, je maakt dan nog steeds gebruik van de resterende gratis
                        proefperiode.<br/>
                        Tot 1,000 orders per maand is deze module +&euro;5 p/m, daarboven +&euro;10 p/m.
                    </p>
                    {renderActivateButton(true)}
                </div>
            </InfoMessage>
        )
    }

    function renderInactiveBanner() {
        return (
            <InfoMessage className='notifications-module-banner'>
                <div className='message-content'>
                    <p className='module-text'>
                        Ontvang meldingen van nieuwe reviews op jouw producten per e-mail.<br/>
                        Zo ben je altijd up-to-date, zodat jij actie kunt ondernemen en geen omzet verliest.<br/><br/>
                        Deze feature is samen met de BolmateAI feature een optionele module.<br/>
                        Tot 1,000 orders per maand is deze module +&euro;5 p/m, daarboven +&euro;10 p/m.
                    </p>
                    {renderActivateButton(false)}
                </div>
            </InfoMessage>
        )
    }

    function renderActivateButton(isTrial: boolean) {
        const label = isTrial ? 'Module toevoegen na proef voor bovenstaande meerprijs' :
            'Feature activeren voor bovenstaande meerprijs';
        return (
            <BolmateButton onClick={onActivateModule} label={label} id='btn-activate' className='btn-activate'/>
        );
    }

    return (
        <>
            {renderModuleBanner()}
            <div className='notifications-page'>
                <Loader active={loading}/>
                <ReviewNotificationsController onSendTestNotification={onSendTestNotification}
                                               onPulseModule={onPulseModule}/>
                <BolmateButton onClick={onClickFeedback} label='Waarvoor wil je nog meer meldingen?...' className='btn-feedback' secondary/>
            </div>
        </>
    )
}